import { useState } from 'react';

// Survey question types
// 1 = Radio (single select), 2 = Checkbox (multi-select), 3 = Text, 4 = Dropdown, 5 = Textarea

export interface SurveyOption {
  optionId: number;
  optionValue: string;
}

export interface SurveyQuestion {
  qid: number;
  question: string;
  qtypeid: number; // 1-5
  mandatory?: boolean;
  options?: SurveyOption[];
}

export interface QuestionResponse {
  qid: number;
  responseText: string;
}

interface SurveyQuestionsProps {
  questions: SurveyQuestion[];
  responses: QuestionResponse[];
  onResponseChange: (qid: number, value: string) => void;
  disabled?: boolean;
}

// (#2088) DEMO_SURVEY_QUESTIONS removed: mock data must never be reachable
// from production paths — a fetch failure now surfaces a retryable error in
// StoreDetail instead of substituting fake questions (diag #48).

function RadioQuestion({
  question,
  value,
  onChange,
  disabled,
}: {
  question: SurveyQuestion;
  value: string;
  onChange: (val: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-2">
      {question.options?.map((option) => (
        <label
          key={option.optionId}
          className={`flex items-center gap-3 rounded-lg border p-3 transition-colors ${
            value === option.optionValue
              ? 'border-primary-500 bg-primary-50'
              : 'border-slate-200 hover:bg-slate-50'
          } ${disabled ? 'opacity-60' : 'cursor-pointer'}`}
        >
          <input
            type="radio"
            name={`q-${question.qid}`}
            value={option.optionValue}
            checked={value === option.optionValue}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-slate-700">{option.optionValue}</span>
        </label>
      ))}
    </div>
  );
}

function CheckboxQuestion({
  question,
  value,
  onChange,
  disabled,
}: {
  question: SurveyQuestion;
  value: string;
  onChange: (val: string) => void;
  disabled?: boolean;
}) {
  const selectedValues = value ? value.split('|') : [];

  const handleToggle = (optionValue: string) => {
    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue)
      : [...selectedValues, optionValue];
    onChange(newValues.join('|'));
  };

  return (
    <div className="space-y-2">
      {question.options?.map((option) => (
        <label
          key={option.optionId}
          className={`flex items-center gap-3 rounded-lg border p-3 transition-colors ${
            selectedValues.includes(option.optionValue)
              ? 'border-primary-500 bg-primary-50'
              : 'border-slate-200 hover:bg-slate-50'
          } ${disabled ? 'opacity-60' : 'cursor-pointer'}`}
        >
          <input
            type="checkbox"
            checked={selectedValues.includes(option.optionValue)}
            onChange={() => handleToggle(option.optionValue)}
            disabled={disabled}
            className="h-4 w-4 rounded text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-slate-700">{option.optionValue}</span>
        </label>
      ))}
    </div>
  );
}

function TextQuestion({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (val: string) => void;
  disabled?: boolean;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder="Enter your answer..."
      className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-700 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-slate-50 disabled:opacity-60"
    />
  );
}

function SelectQuestion({
  question,
  value,
  onChange,
  disabled,
}: {
  question: SurveyQuestion;
  value: string;
  onChange: (val: string) => void;
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-slate-50 disabled:opacity-60"
    >
      <option value="">Select an option...</option>
      {question.options?.map((option) => (
        <option key={option.optionId} value={option.optionValue}>
          {option.optionValue}
        </option>
      ))}
    </select>
  );
}

function TextareaQuestion({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (val: string) => void;
  disabled?: boolean;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder="Enter your notes..."
      rows={4}
      className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-700 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-slate-50 disabled:opacity-60"
    />
  );
}

export default function SurveyQuestions({
  questions,
  responses,
  onResponseChange,
  disabled = false,
}: SurveyQuestionsProps) {
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set(questions.map((q) => q.qid)));

  const getResponse = (qid: number): string => {
    return responses.find((r) => r.qid === qid)?.responseText || '';
  };

  const toggleQuestion = (qid: number) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(qid)) {
      newExpanded.delete(qid);
    } else {
      newExpanded.add(qid);
    }
    setExpandedQuestions(newExpanded);
  };

  const isQuestionAnswered = (question: SurveyQuestion): boolean => {
    const response = getResponse(question.qid);
    return response.length > 0;
  };

  const renderQuestion = (question: SurveyQuestion) => {
    const value = getResponse(question.qid);
    const handleChange = (val: string) => onResponseChange(question.qid, val);

    switch (question.qtypeid) {
      case 1: // Radio
        return <RadioQuestion question={question} value={value} onChange={handleChange} disabled={disabled} />;
      case 2: // Checkbox
        return <CheckboxQuestion question={question} value={value} onChange={handleChange} disabled={disabled} />;
      case 3: // Text
        return <TextQuestion value={value} onChange={handleChange} disabled={disabled} />;
      case 4: // Select
        return <SelectQuestion question={question} value={value} onChange={handleChange} disabled={disabled} />;
      case 5: // Textarea
        return <TextareaQuestion value={value} onChange={handleChange} disabled={disabled} />;
      default:
        return <TextQuestion value={value} onChange={handleChange} disabled={disabled} />;
    }
  };

  const answeredCount = questions.filter((q) => isQuestionAnswered(q)).length;
  const mandatoryCount = questions.filter((q) => q.mandatory).length;
  const mandatoryAnswered = questions.filter((q) => q.mandatory && isQuestionAnswered(q)).length;

  return (
    <div className="rounded-xl bg-white p-4 shadow-card">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
          Survey Questions
        </h3>
        <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
          {answeredCount}/{questions.length} answered
        </span>
      </div>

      {mandatoryAnswered < mandatoryCount && (
        <div className="mb-4 rounded-lg bg-amber-50 p-3 text-xs text-amber-700">
          <span className="font-medium">{mandatoryCount - mandatoryAnswered}</span> required question(s) remaining
        </div>
      )}

      <div className="space-y-3">
        {questions.map((question, index) => {
          const isExpanded = expandedQuestions.has(question.qid);
          const isAnswered = isQuestionAnswered(question);

          return (
            <div key={question.qid} className="rounded-lg border border-slate-200 overflow-hidden">
              <button
                onClick={() => toggleQuestion(question.qid)}
                className={`flex w-full items-center justify-between p-3 text-left transition-colors ${
                  isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                      isAnswered
                        ? 'bg-green-100 text-green-700'
                        : question.mandatory
                        ? 'bg-red-100 text-red-700'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {isAnswered ? (
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      index + 1
                    )}
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-700">
                      {question.question}
                      {question.mandatory && <span className="ml-1 text-red-500">*</span>}
                    </span>
                  </div>
                </div>
                <svg
                  className={`h-5 w-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isExpanded && (
                <div className="border-t border-slate-200 bg-white p-3">
                  {renderQuestion(question)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
